import ToggleButton from "@via/components/toggle-button.tsx";
import {
  MessageBanner,
  BannerSize,
  BannerType,
} from "@via/components/message-banner/message-banner.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { useProject } from "@via/pages/projects/projects.api";
import { useUserGroups, useUserGroupMembers } from "@via/api/user-groups.api";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { USER_ROLES } from "@via/utils/constants.tsx";
import { Permissions } from "common";
import { type FC, useMemo, useEffect } from "react";
import { type UseFormWatch, type UseFormSetValue } from "react-hook-form";

interface RunPermissionsSectionProps {
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
}

const RunPermissionsSection: FC<RunPermissionsSectionProps> = ({
  watch,
  setValue,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const selectedProjectId = watch("projectId");
  const inheritFromProject = watch("inheritFromProject") ?? true;

  // Fetch project details to get permission info
  const { data: projectData, isLoading: isProjectLoading } = useProject(
    selectedProjectId?.toString() ?? ""
  );

  // Fetch group info by groupId if available
  const { data: groupData, isLoading: isGroupLoading } = useUserGroups(
    0,
    100,
    [],
    projectData?.groupId ? [{ id: "id", value: projectData.groupId }] : [],
    !!projectData?.groupId
  );

  // Fetch shared group members if there's a sharedGroupId (for specific users)
  const { data: sharedGroupMembers, isLoading: isSharedGroupMembersLoading } =
    useUserGroupMembers({
      groupId: projectData?.sharedGroupId ?? undefined,
      page: 0,
      pageSize: 100,
      sort: [{ id: "id", desc: true }],
      includePending: true,
      enabled: !!projectData?.sharedGroupId,
    });

  // Determine if we're still loading any data
  const isLoading =
    isProjectLoading ||
    (projectData?.groupId && isGroupLoading) ||
    (projectData?.sharedGroupId && isSharedGroupMembersLoading);

  // Check if project is private
  const isProjectPrivate = useMemo(() => {
    return projectData?.permission === Permissions.UserOwned;
  }, [projectData]);

  // Automatically set inheritFromProject to false when project is private
  useEffect(() => {
    if (isProjectPrivate && !isProjectLoading) {
      setValue("inheritFromProject", false);
    }
  }, [isProjectPrivate, isProjectLoading, setValue]);

  // Determine visibility message based on project permission
  const visibilityMessage = useMemo(() => {
    if (isLoading) return "Loading project permissions...";

    const permission = projectData?.permission;

    // Check if it's Public
    if (permission === Permissions.Public) {
      if (isAdmin) {
        return "Visibility: Public";
      }
      // Only an admin's run may be public: anyone else's run in a public project is
      // shared with the project's group, or kept private when it has none.
      const adminOnlyNote = "Only administrators can share runs publicly.";
      if (projectData?.groupId) {
        const groupName = groupData?.data?.[0]?.name || "group";
        return `Visibility: ${groupName} members will be able to see this run. ${adminOnlyNote}`;
      }
      return `Visibility: Private. ${adminOnlyNote}`;
    }

    // Check if it's GroupShared
    if (permission === Permissions.GroupShared && projectData?.groupId) {
      // Get group name from group data
      const groupName = groupData?.data?.[0]?.name || "group";
      return `Visibility: ${groupName} members will be able to see this run.`;
    }

    // Check if specific users are shared (sharedGroupId exists)
    if (
      permission === Permissions.SpecificUsersShared &&
      projectData?.sharedGroupId
    ) {
      const userList = sharedGroupMembers?.data ?? [];
      if (userList.length > 0) {
        // Format user names - show all users in array format
        const userNames = userList
          .map(u => u.userName || u.email)
          .filter(Boolean);

        if (userNames.length === 0) {
          return "Visibility: Specific users will be able to see this run.";
        }

        // Return array with header message followed by user names
        return [
          "The following users will be able to see this run:",
          ...userNames,
        ];
      }
    }

    // Default to Private (UserOwned)
    return "Visibility: Private";
  }, [projectData, groupData, sharedGroupMembers, isLoading, isAdmin]);

  const bannerTitle = useMemo(() => {
    return inheritFromProject
      ? "This run will inherit visibility from the project"
      : "This run will be private.";
  }, [inheritFromProject]);

  const bannerMessage = useMemo(() => {
    if (inheritFromProject && projectData) {
      // If visibilityMessage is an array, return it as is
      // If it's a string, return it as is
      return visibilityMessage;
    }
    return "";
  }, [inheritFromProject, projectData, visibilityMessage]);

  // Don't show the section if no project is selected
  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="my-5 border-t pt-5">
      <h3 className="mb-4 text-base font-semibold leading-6 text-gray-900">
        Permissions
      </h3>

      {/* Show skeleton loader while project data is loading */}
      {isProjectLoading ? (
        <div className="space-y-4">
          <SkeletonLoader count={1} />
        </div>
      ) : (
        <>
          {/* Banner */}
          <div className="mb-4">
            <MessageBanner
              type={BannerType.Info}
              title={bannerTitle}
              messages={bannerMessage}
              size={BannerSize.Small}
            />
          </div>

          {/* Toggle - Only show if project is not private */}
          {!isProjectPrivate && (
            <label className="flex items-center gap-3">
              <ToggleButton
                isOn={inheritFromProject}
                onToggle={value => setValue("inheritFromProject", value)}
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">
                  Inherit from project
                </span>
                <p className="text-xs text-gray-500">
                  Use project groups as default visibility
                </p>
              </div>
            </label>
          )}
        </>
      )}
    </div>
  );
};

export default RunPermissionsSection;
